import { entities, plainToInstance } from "@fraction/shared";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { DOCUMENTS_KEY } from "src/apps/PostFundedDashboard/queryKeys";
import {
  type CrossWindowCacheChange,
  subscribeToCrossWindowCacheChanges,
  withCrossWindowCacheChangeApplied,
} from "src/lib/runtime/crossWindowCache";
import {
  removeDocumentFromFetchCache,
  SINGLE_APPLICATION_KEY,
  updateDocumentInFetchCache,
} from "./useApplication";

/**
 * Applies cache changes made in this app's other native windows.
 *
 * The counterpart to the publishing in `useApplication`: the same writers, driven by what another
 * window did rather than by a mutation here. Everything arrives as JSON over Tauri's IPC, so a
 * document is rebuilt through the entity before it is stored — otherwise its dates would be strings
 * and its rate a plain number, under a type that says otherwise.
 */
/**
 * Re-read every list that shows a deal's email after one is sent.
 *
 * Called by the composer for the window it sent from and by the applier for all the others, so a
 * sent email lands in the same places whichever window sent it — which is what stopped being true
 * once composing moved into its own window.
 */
export function invalidateDealCommunications(queryClient: QueryClient, applicationId: string) {
  // `["mail", isAuthenticated, applicationId]` — matched by position rather than as a prefix
  // because the authentication flag sits between the two parts we actually know. The single-mail
  // entries (`["mail", id]`) have nothing at index 2, so they fall out of this on their own.
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "mail" && query.queryKey[2] === applicationId,
  });
  // The deal's Comms tab keys its list differently: `["comms", { appId }]`.
  void queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "comms" &&
      (query.queryKey[1] as { appId?: string } | undefined)?.appId === applicationId,
  });
}

export function applyCrossWindowCacheChange(queryClient: QueryClient, change: CrossWindowCacheChange) {
  // Guarded here rather than at the subscription, because applying is what must not re-publish —
  // the writers below are the same ones that publish, and a change echoed back would never settle.
  withCrossWindowCacheChangeApplied(() => applyChange(queryClient, change));
}

function applyChange(queryClient: QueryClient, change: CrossWindowCacheChange) {
  if (change.kind === "document") {
    const document = plainToInstance(entities.UploadedFile, change.document as object);
    // The received document is already the merged result, so the previous value is discarded
    // rather than merged with — the window that made the change is the authority on it.
    updateDocumentInFetchCache(queryClient, change.documentId, change.applicationId, () => document);
    return;
  }
  if (change.kind === "document-removed") {
    removeDocumentFromFetchCache(queryClient, change.documentId, change.applicationId);
    return;
  }
  if (change.kind === "documents") {
    void queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_KEY, change.applicationId] });
    return;
  }
  if (change.kind === "communications") {
    invalidateDealCommunications(queryClient, change.applicationId);
    return;
  }
  if (change.kind === "proposed-communications") {
    void queryClient.invalidateQueries({
      queryKey: ["proposed-communications", change.applicationId],
    });
    return;
  }
  // The application graph travels as an id — see `publishApplicationCacheChange`. Invalidating is
  // both cheaper than shipping it and more truthful: whatever the other window did to the deal, a
  // fresh read is the one thing guaranteed to reflect it.
  void queryClient.invalidateQueries({ queryKey: [...SINGLE_APPLICATION_KEY, change.applicationId] });
}

/** Mount once per window. Inert outside Tauri, where there is only one. */
export function useCrossWindowCacheSync(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;
    void subscribeToCrossWindowCacheChanges((change) => applyCrossWindowCacheChange(queryClient, change))
      .then((unsubscribe) => {
        if (cancelled) unsubscribe();
        else stop = unsubscribe;
      })
      .catch((error) => console.warn("Could not sync caches with the other windows.", error));
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [queryClient]);
}
